import { ButtonBase } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useMemo } from 'react';

import {
  convertStatus,
  convertStatusUrl,
  Status,
} from '../../../../api/enumerations';
import {
  GetAllWorkOrderDataProps,
  GetTopWorkOrdersData,
  GetWorkOrdersParams,
  WorkOrderData,
} from '../../../../api/workOrders/types';
import { Constants } from '../../../../constants/home';
import { IconUnfoldMoreMS } from '../../../../constants/icons';
import { Column } from '../../Column';
import { applyClientSideFilters } from '../../hooks/extendedFilters';
import { usePipelineColumns } from '../../hooks/usePipelineColumns';
import { PipelineContainer, PipelineToolbar } from '../../styles';

interface PipelineViewProps {
  pipelineData: GetAllWorkOrderDataProps | GetTopWorkOrdersData | undefined;
  pipelineLoading: boolean;
  isSearching: boolean;
  filters: GetWorkOrdersParams | undefined;
}

const PIPELINE_STATUSES: Status[] = [
  Status.ENTRANCE,
  Status.PEPT,
  Status.SCHEDULE,
  Status.INSPECTION,
  Status.SAMPLE_CREATION,
  Status.CALCULATION,
  Status.REPORT,
  Status.REVISION,
];

interface ColumnEntry {
  id: string;
  name: string;
  workOrders: WorkOrderData[];
  link: string;
  status: Status;
  totalRecords: number | undefined;
}

// Stable empty list: a new `[]` per render would reset every empty column.
const NO_WORK_ORDERS: WorkOrderData[] = [];

const buildColumns = (
  data: GetAllWorkOrderDataProps | GetTopWorkOrdersData | undefined,
  filters: GetWorkOrdersParams | undefined
): ColumnEntry[] =>
  PIPELINE_STATUSES.map((status) => {
    const slice = data?.[status];
    const isTopShape = !!slice && typeof slice === 'object' && 'items' in slice;
    const rows =
      (isTopShape
        ? (slice as { items?: WorkOrderData[] }).items
        : (slice as WorkOrderData[] | undefined)) ?? NO_WORK_ORDERS;
    return {
      id: convertStatusUrl(status),
      name: convertStatus(status),
      workOrders: filters ? applyClientSideFilters(rows, filters) : rows,
      link: convertStatusUrl(status),
      status,
      totalRecords: isTopShape
        ? (slice as { total_records?: number }).total_records
        : undefined,
    };
  });

const ExpandAllLink = styled(ButtonBase)(({ theme }) => ({
  ...theme.typography.body2,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  color: theme.palette.text.secondary,
  padding: '2px 8px',
  borderRadius: `${theme.palette.tokens.radius.control}px`,
  fontFamily: 'inherit',
  '& .material-symbols-outlined': { fontSize: '16px' },
  '&:hover': {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.action.hover,
  },
}));

export function PipelineView({
  pipelineData,
  pipelineLoading,
  isSearching,
  filters,
}: PipelineViewProps): JSX.Element {
  const columns = useMemo(
    () => buildColumns(pipelineData, filters),
    [pipelineData, filters]
  );
  const { isCollapsed, toggle, expandAll, collapsedCount } =
    usePipelineColumns();

  return (
    <>
      <PipelineToolbar>
        {collapsedCount > 0 && (
          <ExpandAllLink disableTouchRipple onClick={expandAll}>
            {IconUnfoldMoreMS}
            {Constants.expandAllColumns}
          </ExpandAllLink>
        )}
      </PipelineToolbar>
      <PipelineContainer>
        {columns.map((column) => (
          <Column
            key={column.id}
            columnData={column}
            pipelineLoading={pipelineLoading}
            isSearching={isSearching}
            collapsed={isCollapsed(column.status)}
            onToggleCollapse={() => toggle(column.status)}
          />
        ))}
      </PipelineContainer>
    </>
  );
}
